import type { Scene } from '@babylonjs/core/scene';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import type { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { PhotoDome } from '@babylonjs/core/Helpers/photoDome';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { RenderingGroup } from '@babylonjs/core/Rendering/renderingGroup';
import type { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import type { QualityProfile, RenderingConfig } from '../config/rendering.config';
import { DEG_TO_RAD } from '../utils/math';

/**
 * Large flat surfaces only RECEIVE shadows – casting would cost fill rate and cause acne.
 * Tower cranes (alpha-tested lattice 47 m up) would only drop thin lines far away; printed decals
 * (logos, lettering) sit millimetres off their panel – their shadow would show as ghost lettering.
 * Glow effects (FX_…, e.g. the golden bottle's halo) are light, not matter – they cast none.
 */
const RECEIVE_ONLY = /_MESH_(ROAD|SHOULDER|GROUND_SIDES|LANE_MARKINGS|EDGE_LINES)|^SKY|^AD_SURFACE|^FX_|^DEBUG|^SOCKET_GIZMO|^ENV_CRANE_A|_DECALS|^SKM_.*_(Logos|AdBack)$|:SKM_.*_(Logos|AdBack)$/;
/** Shadows of casters outside the shadow box still fall into it (≈ 0.9 × height at 48° sun; shell buildings 16 m). */
const SHADOW_CASTER_REACH = 16;
const BLOOM = { threshold: 0.9, weight: 0.18, kernel: 48, scale: 0.5 };
/** Segments of the skyline band (≈ 3.75° each – invisible at 300 m). */
const SKYLINE_TESSELLATION = 96;
/** Babylon's cylinder starts u = 0 at +x and runs u = 0.5 to -x; a quarter turn puts u = 0.5 at +z (the start direction). */
const SKYLINE_U_HALF_TO_FORWARD = Math.PI / 2;

/**
 * Look of the world: image based lighting from a CC0 HDRI, a photographic sky
 * dome, filmic (ACES) tone mapping, sun shadows that follow the camera and an
 * optional post pipeline (MSAA/FXAA, bloom) – all driven by RenderingConfig and
 * the device quality profile. Engine-specific, no gameplay logic.
 */
export class RenderingEnvironment {
  private readonly shadowGenerator: ShadowGenerator | null = null;
  private readonly horizonGround: Mesh;
  /** Opaque backdrop drawn before the world, in this order (sky dome, horizon ground). */
  private readonly backdrop: AbstractMesh[] = [];
  private readonly lightDirection: Vector3;
  private readonly lightRight: Vector3;
  private readonly lightUp: Vector3;
  private readonly focus = new Vector3();
  private readonly scratch = new Vector3();
  private skyDome: PhotoDome | null = null;
  private skylineMaterial: StandardMaterial | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly sun: DirectionalLight,
    ambient: HemisphericLight,
    private readonly config: RenderingConfig,
    private readonly quality: QualityProfile,
  ) {
    this.lightDirection = sun.direction.clone().normalize();
    this.lightRight = Vector3.Cross(Vector3.Up(), this.lightDirection).normalize();
    this.lightUp = Vector3.Cross(this.lightDirection, this.lightRight).normalize();

    sun.diffuse = Color3.FromHexString(config.sunColor);
    sun.specular = sun.diffuse.clone();

    if (config.environment.enabled) {
      this.setupEnvironment();
      // The HDRI replaces the hemispheric fill light (both together would double the ambient).
      ambient.setEnabled(false);
    }
    this.setupToneMapping();
    this.horizonGround = this.createHorizonGround();
    this.drawBackdropFirst();
    if (quality.shadowMapSize > 0) this.shadowGenerator = this.setupShadows();
  }

  /** Sky dome and skyline band (null without environment) – ZoneLighting dims and tints them at night. */
  get sky(): { readonly dome: PhotoDome | null; readonly skyline: StandardMaterial | null } {
    return { dome: this.skyDome, skyline: this.skylineMaterial };
  }

  /**
   * Attaches post processing to the active camera (after the camera exists). Profiles
   * without MSAA/bloom get no pipeline at all – the canvas anti-aliasing is cheaper
   * and avoids half-float render targets on phones.
   */
  attachCamera(camera: Camera): void {
    if (this.quality.msaaSamples <= 1 && !this.quality.bloom) return;
    const pipeline = new DefaultRenderingPipeline('POST', true, this.scene, [camera]);
    pipeline.samples = this.quality.msaaSamples;
    pipeline.fxaaEnabled = this.quality.msaaSamples <= 1;
    pipeline.imageProcessingEnabled = false; // tone mapping stays per material (sky dome is pre-tonemapped)
    pipeline.bloomEnabled = this.quality.bloom;
    if (this.quality.bloom) {
      pipeline.bloomThreshold = BLOOM.threshold;
      pipeline.bloomWeight = BLOOM.weight;
      pipeline.bloomKernel = BLOOM.kernel;
      pipeline.bloomScale = BLOOM.scale;
    }
  }

  /**
   * Moves the shadow frustum with the view: centered `lookAhead` meters in front
   * of the camera and snapped to whole shadow-map texels (no shimmering edges).
   */
  update(camera: Camera): void {
    this.horizonGround.position.x = camera.globalPosition.x;
    this.horizonGround.position.z = camera.globalPosition.z;
    if (!this.shadowGenerator) return;
    const { extent, depth, lookAhead } = this.config.shadows;
    const forward = this.scratch;
    camera.getDirectionToRef(Vector3.Forward(), forward);
    forward.y = 0;
    if (forward.lengthSquared() > 0) forward.normalize();
    this.focus.copyFrom(camera.globalPosition).addInPlace(forward.scaleInPlace(lookAhead));
    this.focus.y = 0;

    const texel = (2 * extent) / this.quality.shadowMapSize;
    const r = Math.round(Vector3.Dot(this.focus, this.lightRight) / texel) * texel;
    const u = Math.round(Vector3.Dot(this.focus, this.lightUp) / texel) * texel;
    const f = Vector3.Dot(this.focus, this.lightDirection) - depth / 2;
    this.sun.position.set(
      this.lightRight.x * r + this.lightUp.x * u + this.lightDirection.x * f,
      this.lightRight.y * r + this.lightUp.y * u + this.lightDirection.y * f,
      this.lightRight.z * r + this.lightUp.z * u + this.lightDirection.z * f,
    );
  }

  private setupEnvironment(): void {
    const env = this.config.environment;
    const rotation = env.rotationDegrees * DEG_TO_RAD;
    const texture = CubeTexture.CreateFromPrefilteredData(env.envUrl, this.scene);
    texture.rotationY = rotation;
    this.scene.environmentTexture = texture;
    this.scene.environmentIntensity = env.intensity;

    const dome = new PhotoDome('SKY_DOME', this.quality.skyTexture, { resolution: 48, size: env.skyRadius * 2, useDirectMapping: false }, this.scene);
    this.skyDome = dome;
    dome.mesh.applyFog = false;
    dome.mesh.isPickable = false;
    // The sky must travel with the camera. Babylon ignores infiniteDistance on parented meshes,
    // so it is set on the dome's root node; otherwise the runner leaves the sphere after a few
    // hundred meters and only the grey clear color remains.
    dome.infiniteDistance = true;
    dome.mesh.alwaysSelectAsActiveMesh = true;
    this.backdrop.push(dome.mesh);
    dome.rotation.y = rotation;
    // The sky image is already tonemapped – keep it out of the scene's ACES curve.
    const skyProcessing = new ImageProcessingConfiguration();
    skyProcessing.toneMappingEnabled = false;
    skyProcessing.isEnabled = false;
    dome.material.imageProcessingConfiguration = skyProcessing;
    if (env.skyline) this.createSkyline(dome, env.skyline, skyProcessing, rotation);
  }

  /**
   * City skyline: an open band textured with an angular panorama (u = azimuth, v = elevation),
   * child of the sky dome so it travels with the camera like an infinitely distant backdrop.
   * Unlit and untonemapped like the sky; drawn first among the transparent meshes.
   */
  private createSkyline(
    dome: TransformNode,
    skyline: NonNullable<RenderingConfig['environment']['skyline']>,
    processing: ImageProcessingConfiguration,
    domeRotation: number,
  ): void {
    const [low, high] = skyline.elevationDegrees;
    const bottom = skyline.radius * Math.tan(low * DEG_TO_RAD);
    const top = skyline.radius * Math.tan(high * DEG_TO_RAD);
    const band = CreateCylinder(
      'SKY_SKYLINE',
      { height: top - bottom, diameter: 2 * skyline.radius, tessellation: SKYLINE_TESSELLATION, cap: Mesh.NO_CAP },
      this.scene,
    );
    band.parent = dome;
    band.position.y = (top + bottom) / 2;
    band.rotation.y = SKYLINE_U_HALF_TO_FORWARD - domeRotation;
    band.isPickable = false;
    band.applyFog = false;
    band.alwaysSelectAsActiveMesh = true;
    band.alphaIndex = 0;

    const texture = new Texture(skyline.url, this.scene);
    texture.hasAlpha = true;
    texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    const material = new StandardMaterial('MAT_SKYLINE', this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.disableLighting = true;
    material.emissiveColor = Color3.White();
    material.specularColor = Color3.Black();
    material.backFaceCulling = false;
    material.fogEnabled = false;
    material.imageProcessingConfiguration = processing;
    band.material = material;
    this.skylineMaterial = material;
  }

  private createHorizonGround(): Mesh {
    const { color, size, height } = this.config.horizonGround;
    const ground = CreateGround('SKY_HORIZON_GROUND', { width: size, height: size }, this.scene);
    ground.position.y = height;
    ground.isPickable = false;
    ground.alwaysSelectAsActiveMesh = true; // follows the camera, always around it
    const material = new PBRMaterial('MAT_HORIZON_GROUND', this.scene);
    material.albedoColor = Color3.FromHexString(color).toLinearSpace();
    material.metallic = 0;
    material.roughness = 1;
    // No Fresnel sky reflection at grazing angles – from above it otherwise reads as a lake.
    material.metallicF0Factor = 0;
    ground.material = material;
    // Backdrop: no depth writes, so everything drawn after it – also the inside of a pit below
    // this plane – covers it (see drawBackdropFirst).
    material.disableDepthWrite = true;
    this.backdrop.push(ground);
    return ground;
  }

  /**
   * Opaque render order: sky dome, then horizon ground, then the world (Babylon's default painter
   * sort). The ground only hides the lower half of the sky dome; track geometry below ground level
   * (pits) stays visible.
   */
  private drawBackdropFirst(): void {
    const rank = (subMesh: SubMesh): number => {
      const index = this.backdrop.indexOf(subMesh.getMesh());
      return index < 0 ? this.backdrop.length : index;
    };
    const groupId = this.horizonGround.renderingGroupId;
    this.scene.setRenderingOrder(groupId, (a, b) => rank(a) - rank(b) || RenderingGroup.PainterSortCompare(a, b));
  }

  private setupToneMapping(): void {
    const ip = this.scene.imageProcessingConfiguration;
    ip.toneMappingEnabled = this.config.toneMapping.enabled;
    ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    ip.exposure = this.config.toneMapping.exposure * this.quality.exposureScale; // phones: no bloom glow – a bit brighter
    ip.contrast = this.config.toneMapping.contrast;
  }

  private setupShadows(): ShadowGenerator {
    const { extent, depth, bias, normalBias, darkness } = this.config.shadows;
    const sun = this.sun;
    sun.autoUpdateExtends = false;
    sun.autoCalcShadowZBounds = false;
    sun.orthoLeft = -extent;
    sun.orthoRight = extent;
    sun.orthoBottom = -extent;
    sun.orthoTop = extent;
    sun.shadowMinZ = 0;
    sun.shadowMaxZ = depth;

    const generator = new ShadowGenerator(this.quality.shadowMapSize, sun);
    generator.usePercentageCloserFiltering = true;
    generator.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
    generator.bias = bias;
    generator.normalBias = normalBias;
    generator.setDarkness(darkness);
    const map = generator.getShadowMap();
    // Instances cast through their (hidden) source mesh – Babylon batches them in the shadow pass too.
    // The shadow pass has no frustum culling of its own: without the distance test it would draw
    // the whole track 160 m ahead every frame although the shadow box covers only ±extent.
    const reach = extent * Math.SQRT2 + SHADOW_CASTER_REACH;
    if (map) {
      map.renderListPredicate = (mesh: AbstractMesh) => {
        if (!mesh.isEnabled() || !mesh.isVisible || RECEIVE_ONLY.test(mesh.name)) return false;
        const sphere = mesh.getBoundingInfo().boundingSphere;
        const dx = sphere.centerWorld.x - this.focus.x;
        const dz = sphere.centerWorld.z - this.focus.z;
        const range = reach + sphere.radiusWorld;
        return dx * dx + dz * dz <= range * range;
      };
    }
    const receive = (mesh: AbstractMesh) => {
      // receiveShadows lives on the source mesh; instances inherit it (setting it on them only warns).
      if (!mesh.isAnInstance && !mesh.name.startsWith('SKY')) mesh.receiveShadows = true;
    };
    this.scene.onNewMeshAddedObservable.add(receive);
    for (const mesh of this.scene.meshes) receive(mesh);
    return generator;
  }
}